import { Box, Flex, Text } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FiArrowRight } from 'react-icons/fi';
import { useState, useEffect } from 'react';

import { PLAN_TEXT } from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  getDistinctCSV,
  getFormValues,
  handleActionStepNext,
  resetColumnIndicatorMappedData,
  setRowsIndicatorMappedData,
  setSelectedAreasProperties,
} from 'redux/DataImportSlice';
import { fetchBlock, getBlockInputIndicator } from 'redux/PlanPageSlice';

import colors from '../../../dataTheme';
import ActionStepFooter from '../../ActionStepFooter';
import {
  FieldError,
  FieldLabel,
  StepFooterRow,
  StepHeading,
  StepNotice,
  StepSelect,
} from '../../actionStepUi';

const validationSchema = Yup.object().shape({
  rowsIndicatorTableColumn: Yup.string().required('Time value is required'),
});

const RowsType = () => {
  const dispatch: any = useDispatch();
  const distinctCSVValues = useSelector(getDistinctCSV);
  const blockInputIndicator = useSelector(getBlockInputIndicator) ?? [];
  const getFormValue = useSelector(getFormValues);
  const [formValue, setFormValue] = useState(getFormValue?.rowsIndicatorMappedData || {});
  const [selectedColumn, setSelectedColumn] = useState<any>(null);
  const modifyFormValue = (key: any, value: any) => {
    const object: any = JSON.parse(JSON.stringify(formValue));
    object[`${key}`] = value;
    setFormValue(object);
  };

  useEffect(() => {
    const selectedBlockId = getFormValue?.blockId;
    if (selectedBlockId) {
      dispatch(fetchBlock(Number(selectedBlockId)));
    }
  }, [getFormValue?.blockId]);

  useEffect(() => {
    if (getFormValue?.rowsIndicatorTableColumn) {
      const foundItem: any = distinctCSVValues.find((column: any) => {
        return column.current === getFormValue?.rowsIndicatorTableColumn;
      });
      if (foundItem) setSelectedColumn(foundItem);
    }
  }, [getFormValue?.rowsIndicatorTableColumn]);

  const getIndicatorInput = (id: number) => {
    const data = blockInputIndicator.find((item: any) => item.id === Number(id));

    return data?.name ?? '';
  };
  useEffect(() => {
    if (selectedColumn && !(formValue && Object.keys(formValue)?.length)) {
      const updatedFormValue = { ...formValue };

      selectedColumn?.distinctValues?.forEach((item: any) => {
        if (item !== undefined && item !== '\n') {
          const trimmedItem = item.trim(); // Trim any extra spaces

          updatedFormValue[item] = '';
          const matchingBlock = blockInputIndicator.find(
            (block: any) => block.name.trim().toLowerCase() === trimmedItem.toLowerCase(),
          );

          if (matchingBlock) {
            updatedFormValue[item] = matchingBlock.id;
          }
        }
      });

      setFormValue(updatedFormValue);
    }
  }, [selectedColumn, formValue, blockInputIndicator]);

  const handleSubmit = (form: any, setSubmitting: (data: boolean) => void) => {
    dispatch(setRowsIndicatorMappedData(form)).then(() => {
      dispatch(resetColumnIndicatorMappedData());
      setSubmitting(false);
      dispatch(handleActionStepNext());
    });
  };

  return (
    <Box w='100%'>
      <StepHeading
        title='Select indicators'
        subtitle='Select the column in your table that relates to your indicators.'
      />
      <Formik
        initialValues={{
          rowsIndicatorTableColumn: selectedColumn?.current ?? '',
          data: formValue,
        }}
        validationSchema={validationSchema}
        onSubmit={(form, { setSubmitting }) => handleSubmit(form, setSubmitting)}
        enableReinitialize={true}
      >
        {({ values, errors, setFieldValue, isSubmitting }: any) => (
          <Form style={{ width: '100%' }}>
            <FieldLabel>Indicator column</FieldLabel>
            <StepSelect
              value={values.rowsIndicatorTableColumn}
              placeholder='Select column'
              isInvalid={Boolean(errors.rowsIndicatorTableColumn)}
              options={(distinctCSVValues ?? []).map((item: any) => ({
                value: item?.current,
                label: item?.modified,
              }))}
              onChange={(value) => {
                const item = (distinctCSVValues ?? []).find(
                  (entry: any) => entry.current === value,
                );
                setFieldValue('rowsIndicatorTableColumn', value);
                setSelectedColumn(item ?? null);
                dispatch(setSelectedAreasProperties({ rowsIndicatorTableColumn: value }));
              }}
            />
            <ErrorMessage name='rowsIndicatorTableColumn' component={FieldError} />

            <Box mt={5}>
              <StepHeading title='Map the values into Blox' />
            </Box>
            {Object.keys(formValue).length > 0 && (
              <Flex alignItems='center' gap={3} mb={3} mt='-8px'>
                <FieldLabel flex={1} mb={0}>
                  Name in file
                </FieldLabel>
                <Box w='16px' />
                <FieldLabel flex={1} mb={0}>
                  Indicator in Blox
                </FieldLabel>
              </Flex>
            )}

            {selectedColumn?.distinctValues?.length > 0 ? (
              <Flex direction='column' gap={3} maxH='25vh' overflowY='auto' className='scroll'>
                {selectedColumn.distinctValues
                  .filter((item: any) => item !== undefined && item !== '\n')
                  .map((item: any) => (
                    <Flex key={item} alignItems='center' gap={3}>
                      <Text
                        flex={1}
                        minW={0}
                        {...PLAN_TEXT.bodyMedium}
                        color={colors.textDark}
                        noOfLines={1}
                        title={item}
                      >
                        {item}
                      </Text>
                      <FiArrowRight size={16} color={colors.textSecondary} />
                      <Box flex={1} minW={0}>
                        <StepSelect
                          value={
                            values?.data[item] && getIndicatorInput(values?.data[item])
                              ? values.data[item]
                              : ''
                          }
                          placeholder='Select indicator'
                          isClearable
                          options={blockInputIndicator
                            .filter(
                              (header: any) =>
                                header?.id === values?.data[item] ||
                                !Object.values(formValue).includes(header?.id),
                            )
                            .map((header: any) => ({ value: header.id, label: header.name }))}
                          onChange={(role) => {
                            modifyFormValue(item, role);
                            setFieldValue(item, role);
                          }}
                        />
                      </Box>
                    </Flex>
                  ))}
              </Flex>
            ) : (
              <StepNotice>No properties found</StepNotice>
            )}

            <StepFooterRow>
              <ActionStepFooter
                nextButtonName='Continue'
                prevButtonName='Previous'
                isSubmitting={isSubmitting}
              />
            </StepFooterRow>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default RowsType;
